import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Product } from "./types";

interface CartItem {
  product: Product;
  quantity: number;
}

interface CartCtx {
  items: CartItem[];
  add: (p: Product, qty?: number) => boolean;  // true if newly added
  update: (id: string, qty: number) => void;
  remove: (id: string) => void;
  clear: () => void;
  subtotal: number;
  count: number;
}

const Ctx = createContext<CartCtx | null>(null);
const KEY = "b2bmall:cart:v1";

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try { const raw = localStorage.getItem(KEY); if (raw) setItems(JSON.parse(raw)); } catch {}
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items, hydrated]);

  const add = useCallback((p: Product, qty = 1): boolean => {
    let added = false;
    setItems((prev) => {
      if (prev.some((it) => it.product.id === p.id)) return prev;   // 이미 담김 — 수량 변경은 Cart 페이지에서
      added = true;
      return [...prev, { product: p, quantity: qty }];
    });
    return added;
  }, []);

  const update = useCallback((id: string, qty: number) => {
    setItems((prev) => prev.map((it) => (it.product.id === id ? { ...it, quantity: Math.max(1, qty) } : it)));
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.product.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(() => items.reduce((sum, it) => sum + it.product.price * it.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((n, it) => n + it.quantity, 0), [items]);

  return <Ctx.Provider value={{ items, add, update, remove, clear, subtotal, count }}>{children}</Ctx.Provider>;
}

export function useCart() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCart outside CartProvider");
  return v;
}
